import { FormModal } from "../FormModal";
import { 
  Container, 
  FixCoinValue, 
  Label, 
  FixcoinBalance,
  PaymentMethodGrid,
  PaymentMethodCard,
  WarningLabel,
  PlanTitle,
  PixBox,
  QrCodeImage,
  CopyPasteContainer,
  CopyButton,
  Spinner
} from "./styles";
import { CopyPaste, QrCodeContainer, SentToMailTextContainer } from "../BillPaymentDetails/styles";
import { useEffect, useState, useRef } from "react";
import { i18n } from "../../localization/i18n";
import { showToast } from "../CustomToast";
import { BiCopy } from "react-icons/bi";
import { FaBarcode } from "react-icons/fa";
import { MdPix } from "react-icons/md";
import { FaPix } from "react-icons/fa6";
import { FiCopy } from "react-icons/fi";
import { typesErrors } from "../../utils/validateFormFields/yupErrors";
import * as Yup from "yup";
import api from "../../services/api";
import { ICurrencyInfos, useAuth } from "../../hooks/AuthContext";
import validateFormFields from "../../utils/validateFormFields";
import { FixcoinItens } from "../FixcoinItens";
import { PaymentTitle } from "./styles";
import ReactLoading from "react-loading";

interface IOptions {
  optionText: string;
  value: string;
}

interface IPaymentDetails {
  onHandleCancel: () => void;
  isOpen: boolean;
  currency: ICurrencyInfos | undefined;
  licenseeFixcoins: number;
  onSuccess?: () => void;
}

interface IFixcoinCards {
  price: string;
}

export function FixcoinPaymentDetails({
  onHandleCancel,
  isOpen,
  currency,
  licenseeFixcoins
}: IPaymentDetails) {
  const { user } = useAuth();

  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [pixQrCodeUrl, setPixQrCodeUrl] = useState<string>();
  const [pixQrCodeKey, setPixQrCodeKey] = useState<string>();
  const [boletoUrl, setBoletoUrl] = useState<string>();
  const [boletoKey, setBoletoKey] = useState<string>();
  const [selectedValue, setSelectedValue] = useState<string>("500");
  const [selectedCard, setSelectedCard] = useState<number>(2);

  const [pixGenerated, setPixGenerated] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [isPollingPix, setIsPollingPix] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fixcoinCards: IFixcoinCards[] = [
    { price: "100" },
    { price: "200" },
    { price: "500" },
    { price: "1000" },
    { price: "2000" },
    { price: "3000" },
  ]

  function clearData() {
    setPaymentMethod("pix");
    setLoading(false);
    setErrors({} as any);
    setSelectedValue("500");
    setSelectedCard(2);
    setPixGenerated(false);
    setPixQrCodeUrl(undefined);
    setPixQrCodeKey(undefined);
    setTransactionId("");
    setIsPollingPix(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }

  useEffect(() => {
    clearData();
  }, [isOpen]);

  useEffect(() => {
    if (pixGenerated && transactionId) {
      setIsPollingPix(true);
      
      intervalRef.current = setInterval(async () => {
        try {
          // This should match the payment status route on backend, you can adapt it to the real path
          const response = await api.get(`/transactions/status/${transactionId}`);
          
          if (response.data.status === "PAID" || response.data.status === "CONFIRMED" || response.data.status === "RECEIVED") {
            if (intervalRef.current) clearInterval(intervalRef.current);
            setIsPollingPix(false);
            
            showToast({ type: "success", message: "Pagamento confirmado! FixCoins adicionados à sua carteira." });
            
            onHandleCancel();
            onSuccess?.();
          }
        } catch (err) {
          console.log("Aguardando confirmação de pagamento...");
        }
      }, 5000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        setIsPollingPix(false);
      }
    };
  }, [pixGenerated, transactionId]);

  async function payBill() {
    setLoading(true);

    try {
      const body = {
        payment_method: paymentMethod,
        quantity: selectedValue,
        price: (Number(selectedValue) * (currency?.fixcoin_exchange ?? 1)).toFixed(2),
        licensee_id: user.person.licensee.id,
      };

      const yup_validation = Yup.object().shape({
        payment_method: Yup.string(),
      });

      const validation = await validateFormFields(body, yup_validation, {
        payment_method: "",
      });

      if (validation.status === 400) {
        setErrors(validation.errors);
        showToast({
          type: "error",
          message: i18n.t("form_errors.alert"),
        });

        setLoading(false);
        return;
      }

      setErrors({});

      await api
        .post(`/transactions/buy`, body)
        .then((response) => {
          if (paymentMethod === "pix") {
            const data = response.data;
            console.log("PAYLOAD DO PIX COMPLETO:", data);
            
            // Depending on Asaas return shape from our backend
            const qrCode = data.pix_qr_code || data.charge?.pix_qr_code || data.encodedImage || data.charge?.encodedImage || data.qrCode?.encodedImage;
            const copyPaste = data.pix_copy_paste || data.charge?.pix_copy_paste || data.payload || data.charge?.payload || data.qrCode?.payload;
            const transId = data.id || data.charge?.id || data.transaction_id;
            
            if (!qrCode || !copyPaste) {
              throw new Error(`Dados do PIX incompletos no retorno do back-end. Verifique o console.`);
            }

            setPixQrCodeUrl(qrCode);
            setPixQrCodeKey(copyPaste);
            setTransactionId(transId);
            setPixGenerated(true);
          } else {
            window.open(response.data.charge?.invoiceUrl || response.data.invoiceUrl, '_blank');
            onHandleCancel();
          }
        });
    } catch (error: any) {
      const axiosErrorMessage = error?.response?.data?.message;
      const fallbackErrorMessage = error?.message || "Erro desconhecido";

      if (axiosErrorMessage === "Card not valid") {
        showToast({
          type: "error",
          message: `${i18n.t("bills.error.transaction")}`,
        });
        return;
      } else if (axiosErrorMessage === "Insufficient funds") {
        showToast({
          type: "error",
          message: `${i18n.t("bills.error.insufficientFunds")}`,
        });
        return;
      }

      showToast({
        type: "error",
        message: axiosErrorMessage || fallbackErrorMessage,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <FormModal
      open={isOpen}
      onRequestClose={onHandleCancel}
      title="Comprar FixCoins"
      width={600}
      showAddAnother={false}
      onSave={pixGenerated ? undefined : payBill}
      onCancel={onHandleCancel}
      saveText={pixGenerated ? 'Aguardando pagamento...' : (paymentMethod === 'pix' ? 'Gerar Pix' : 'Gerar Boleto')}
      cancelText={pixGenerated ? "Fechar" : "Cancelar"}
      isSaveLoading={loading}
      isSaveDisabled={loading || !selectedValue || pixGenerated || !!boletoUrl}
      hideSaveButton={pixGenerated}
    >
      <Container>
        {!pixGenerated && !boletoUrl && !boletoKey &&
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <FixcoinItens
                fixcoinPrices={fixcoinCards}
                setSelectedValue={setSelectedValue}
                setSelectedCard={setSelectedCard}
                selectedCard={selectedCard}
              />
              
              <PaymentTitle>Escolha como deseja pagar</PaymentTitle>

              <PaymentMethodGrid>
                <PaymentMethodCard 
                  isSelected={paymentMethod === "boleto"}
                  onClick={() => setPaymentMethod("boleto")}
                >
                  <PlanTitle>
                    <FaBarcode style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Boleto
                  </PlanTitle>
                </PaymentMethodCard>

                <PaymentMethodCard 
                  isSelected={paymentMethod === "pix"}
                  onClick={() => setPaymentMethod("pix")}
                >
                  <PlanTitle>
                    <MdPix style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Pix
                  </PlanTitle>
                </PaymentMethodCard>
              </PaymentMethodGrid>

              {paymentMethod === "boleto" && (
                <WarningLabel>
                  <b>AVISO:</b> Pagamentos via boleto demoram até 48h para compensação e liberação dos FixCoins.
                </WarningLabel>
              )}

              <div style={{ display: "flex", flexDirection: "row", gap: "1rem", justifyContent: "center", marginTop: "8px" }}>
                <FixcoinBalance>
                  <Label>Valor da compra:</Label>
                  <FixCoinValue>
                    {(Number(selectedValue) * (currency?.fixcoin_exchange ?? 1)).toFixed(2)} {currency?.code}
                  </FixCoinValue>
                </FixcoinBalance>

                <FixcoinBalance>
                  <Label>Saldo pós-compra:</Label>
                  <FixCoinValue>
                    {Number(licenseeFixcoins) + Number(selectedValue)} {`${i18n.t("topbar.coinFix.plural")}`}
                  </FixCoinValue>
                </FixcoinBalance>
              </div>
            </div>
          </>
        }

        <>
          {boletoKey && boletoUrl ? (
            <QrCodeContainer>
              <a href={`${boletoUrl}`} target="_blank" rel="noreferrer">
                {`${i18n.t("bills.linkToAccessTicket")}`}
              </a>
              <CopyPaste>
                <div className="Header">
                  <span>{`${i18n.t("bills.barCode")}`}</span>
                  <BiCopy
                    size={20}
                    onClick={() =>
                      navigator.clipboard.writeText(boletoKey ?? "")
                    }
                  />
                </div>
                {boletoKey}
              </CopyPaste>
              <SentToMailTextContainer>
                <span>{`*${i18n.t("bills.dataSentByMail")}*`}</span>
              </SentToMailTextContainer>
            </QrCodeContainer>
          ) : pixGenerated ? (
            <PixBox>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "var(--fixit)" }}>
                <FaPix size={24} />
                <h3 style={{ margin: 0, fontSize: "18px", color: "var(--black)" }}>Pagamento via Pix</h3>
              </div>
              <p style={{ margin: 0, fontSize: "14px", color: "var(--gray)", textAlign: "center", lineHeight: 1.5 }}>
                Escaneie o QR Code abaixo com o aplicativo do seu banco ou copie o código Pix.
              </p>

              <QrCodeImage 
                src={pixQrCodeUrl?.startsWith?.("data:image") ? pixQrCodeUrl : `data:image/png;base64,${pixQrCodeUrl || ""}`} 
                alt="PIX QRCODE" 
              />
              
              <CopyPasteContainer>
                <div style={{ wordBreak: "break-all", fontSize: "12px", marginBottom: "8px" }}>
                  {pixQrCodeKey || "Código Copia e Cola não disponível"}
                </div>
                <CopyButton onClick={() => {
                  navigator.clipboard.writeText(pixQrCodeKey ?? "");
                  showToast({ type: "success", message: "Código copiado!" });
                }}>
                  <FiCopy size={16} /> Copiar
                </CopyButton>
              </CopyPasteContainer>

              {isPollingPix && (
                <div style={{
                  marginTop: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  color: "var(--fixit)",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}>
                  <Spinner style={{ width: "16px", height: "16px", borderTopColor: "var(--fixit)", borderRightColor: "var(--fixit)" }} />
                  Aguardando pagamento...
                </div>
              )}
            </PixBox>
          ) : (
             <div style={{ display: 'none' }} />
          )}
        </>
      </Container>
    </FormModal>
  )
}
